import React, { useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/AuthContext";
import { tasksService, reportsService, storageService } from "@/services";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Briefcase,
  CheckCircle2,
  DollarSign,
  Calendar,
  MapPin,
  Upload,
  AlertCircle,
  Clock,
  Loader2
} from "lucide-react";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { toast } from "sonner";

const categoryIcons = {
  pothole: "🕳️",
  blocked_drain: "🚰",
  waste_collection: "🗑️",
  street_lighting: "💡",
  flooding: "🌊",
  vandalism: "⚠️",
  illegal_dumping: "🚮",
  broken_infrastructure: "🔧",
  water_leak: "💧",
  other: "📍"
};

export default function MyTasks() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [completingTask, setCompletingTask] = React.useState(null);
  const [completionNotes, setCompletionNotes] = React.useState("");
  const [completionPhotos, setCompletionPhotos] = React.useState([]);
  const [uploadingPhotos, setUploadingPhotos] = React.useState(false);
  const paymentTimersRef = useRef({});

  const { user } = useAuth();

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ['myTasks', user?.email],
    queryFn: () => tasksService.filter({ contractor_email: user?.email }, '-created_date'),
    enabled: !!user,
    initialData: [],
  });

  const { data: reports = [] } = useQuery({
    queryKey: ['taskReports'],
    queryFn: async () => {
      if (tasks.length === 0) return [];
      return await reportsService.list();
    },
    enabled: tasks.length > 0,
    initialData: [],
  });

  // Simulate payment processing for tasks in "awaiting_payment" status
  useEffect(() => {
    tasks.forEach(task => {
      if (task.status === 'awaiting_payment' && !paymentTimersRef.current[task.id]) {
        // Calculate time remaining until payment
        const completedTime = new Date(task.completed_date).getTime();
        const now = Date.now();
        const timeElapsed = now - completedTime;
        const timeRemaining = Math.max(0, 15000 - timeElapsed); // 15 seconds

        paymentTimersRef.current[task.id] = setTimeout(async () => {
          try {
            await tasksService.update(task.id, {
              status: 'paid_externally',
              payment_processed_date: new Date().toISOString(),
            });
            queryClient.invalidateQueries({ queryKey: ['myTasks'] });
            toast.success(`💰 Payment of R${task.payment_amount} has been processed!`, {
              duration: 5000,
            });
          } catch (error) {
            console.error('Error processing payment:', error);
          }
          delete paymentTimersRef.current[task.id];
        }, timeRemaining);
      }
    });

    // Cleanup timers on unmount
    return () => {
      Object.values(paymentTimersRef.current).forEach(timer => clearTimeout(timer));
    };
  }, [tasks, queryClient]);

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingPhotos(true);
    const uploadedUrls = [];

    try {
      for (const file of files) {
        const { file_url } = await storageService.uploadFile(file, 'report-photos');
        uploadedUrls.push(file_url);
      }
      setCompletionPhotos(prev => [...prev, ...uploadedUrls]);
      toast.success(`${uploadedUrls.length} photo${uploadedUrls.length > 1 ? 's' : ''} uploaded!`, { duration: 2000 });
    } catch (error) {
      console.error("Error uploading photo:", error);
      toast.error("Failed to upload photo. Please try again.");
    }

    setUploadingPhotos(false);
  };

  const updateTaskStatusMutation = useMutation({
    mutationFn: async ({ taskId, status }) => {
      return await tasksService.update(taskId, { status });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['myTasks'] });
      toast.success(`✅ Task marked as "${variables.status.replace('_', ' ')}"!`, { duration: 3000 });
    },
    onError: (error) => {
      toast.error("Failed to update task status. Please try again.");
      console.error("Error updating task status:", error);
    }
  });

  const completeTaskMutation = useMutation({
    mutationFn: async () => {
      if (!completionNotes.trim()) {
        throw new Error("Completion notes are required");
      }

      // Update task to awaiting_payment
      await tasksService.update(completingTask.id, {
        status: 'awaiting_payment',
        completed_date: new Date().toISOString(),
        completion_notes: completionNotes,
        completion_photos: completionPhotos,
      });

      // Update report status to resolved
      await reportsService.update(completingTask.report_id, {
        status: 'resolved',
        // resolved_date doesn't exist in schema, update_at is auto-updated
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myTasks'] });
      queryClient.invalidateQueries({ queryKey: ['taskReports'] });
      queryClient.invalidateQueries({ queryKey: ['allReports'] });
      toast.success("💼 Job submitted for payment! Processing will begin shortly.", {
        duration: 5000,
      });
      setCompletingTask(null);
      setCompletionNotes("");
      setCompletionPhotos([]);
    },
    onError: (error) => {
      if (error.message === "Completion notes are required") {
        toast.error("Please add completion notes before submitting");
      } else {
        toast.error("Failed to complete task. Please try again.");
      }
      console.error("Error completing task:", error);
    },
  });

  const tasksWithReports = tasks.map(task => ({
    ...task,
    report: reports.find(r => r.id === task.report_id)
  })).filter(t => t.report);

  const stats = {
    total: tasksWithReports.length,
    in_progress: tasksWithReports.filter(t => t.status === 'claimed' || t.status === 'in_progress').length,
    completed: tasksWithReports.filter(t => t.status === 'paid_externally').length,
    totalEarnings: tasksWithReports.reduce((sum, t) => sum + (t.payment_amount || 0), 0),
    completedEarnings: tasksWithReports.filter(t => t.status === 'paid_externally').reduce((sum, t) => sum + (t.payment_amount || 0), 0),
    awaitingPayment: tasksWithReports.filter(t => t.status === 'awaiting_payment').reduce((sum, t) => sum + (t.payment_amount || 0), 0),
  };

  const completionRate = stats.total > 0 ? ((stats.completed / stats.total) * 100).toFixed(0) : 0;

  // Redirect non-contractors to MapView (role refresh will fix this within 30s)
  if (!user || user.user_role !== 'contractor') {
    navigate(createPageUrl("MapView"));
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 p-4 md:p-8 overflow-y-auto">
      <div className="max-w-7xl mx-auto pb-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Briefcase className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">My Dashboard</h1>
          </div>
          <p className="text-gray-600 dark:text-gray-300">
            Track your tasks, earnings, and performance
          </p>
        </div>

        {/* Performance Summary */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card className="p-4 bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-700">
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Total Tasks</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/20 dark:to-gray-800 border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-600 dark:text-blue-400 mb-1">In Progress</p>
            <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{stats.in_progress}</p>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-green-50 to-white dark:from-green-900/20 dark:to-gray-800 border-green-200 dark:border-green-800">
            <p className="text-sm text-green-600 dark:text-green-400 mb-1">Paid</p>
            <p className="text-2xl font-bold text-green-700 dark:text-green-300">{stats.completed}</p>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/20 dark:to-gray-800 border-emerald-200 dark:border-emerald-800">
            <p className="text-sm text-emerald-600 dark:text-emerald-400 mb-1">Total Earnings</p>
            <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">
              R{stats.totalEarnings.toLocaleString()}
            </p>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-purple-50 to-white dark:from-purple-900/20 dark:to-gray-800 border-purple-200 dark:border-purple-800">
            <p className="text-sm text-purple-600 dark:text-purple-400 mb-1">Success Rate</p>
            <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">{completionRate}%</p>
          </Card>
        </div>

        {/* Earnings Overview */}
        <Card className="p-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm mb-6">
          <h3 className="font-bold text-lg mb-4 text-gray-900 dark:text-white">Earnings Overview</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/20 dark:to-gray-700 rounded-lg">
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Paid Earnings</p>
              <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                R{stats.completedEarnings.toLocaleString()}
              </p>
            </div>
            <div className="p-4 bg-gradient-to-br from-amber-50 to-white dark:from-amber-900/20 dark:to-gray-700 rounded-lg">
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Awaiting Payment</p>
              <p className="text-xl font-bold text-amber-600 dark:text-amber-400">
                R{stats.awaitingPayment.toLocaleString()}
              </p>
            </div>
            <div className="p-4 bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/20 dark:to-gray-700 rounded-lg">
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Avg. per Task</p>
              <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                R{stats.total > 0 ? Math.round(stats.totalEarnings / stats.total).toLocaleString() : 0}
              </p>
            </div>
          </div>
        </Card>

        {/* Tasks List */}
        <Card className="p-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm">
          <h3 className="font-bold text-lg mb-4 text-gray-900 dark:text-white">My Tasks</h3>
          
          {isLoading ? (
            <div className="grid md:grid-cols-2 gap-4">
              {[1, 2].map((i) => (
                <Card key={i} className="p-6 animate-pulse bg-gray-100 dark:bg-gray-700">
                  <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-3/4 mb-4" />
                  <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-full mb-2" />
                  <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-2/3" />
                </Card>
              ))}
            </div>
          ) : tasksWithReports.length === 0 ? (
            <div className="text-center py-12">
              <Briefcase className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No Tasks Yet</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Claim your first job to get started
              </p>
              <Button
                onClick={() => navigate(createPageUrl("ContractorJobs"))}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                Browse Available Jobs
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {tasksWithReports.map((task) => {
                const getStatusBadge = () => {
                  if (task.status === 'paid_externally') {
                    return (
                      <Badge className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                        <DollarSign className="w-3 h-3 mr-1" />
                        Paid Externally
                      </Badge>
                    );
                  } else if (task.status === 'awaiting_payment') {
                    return (
                      <Badge className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                        <Clock className="w-3 h-3 mr-1 animate-pulse" />
                        Awaiting Payment
                      </Badge>
                    );
                  } else if (task.status === 'in_progress') {
                    return (
                      <Badge className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                        In Progress
                      </Badge>
                    );
                  } else {
                    return (
                      <Badge className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                        {task.status === 'claimed' ? 'Claimed' : task.status.replace('_', ' ')}
                      </Badge>
                    );
                  }
                };

                return (
                  <motion.div
                    key={task.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Card className="p-6 bg-white dark:bg-gray-700">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-start gap-3 flex-1">
                          <span className="text-3xl">{categoryIcons[task.report?.category]}</span>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-bold text-gray-900 dark:text-white mb-1">
                              {task.report?.title}
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                              {task.report?.description}
                            </p>
                          </div>
                        </div>
                        {getStatusBadge()}
                      </div>

                      {/* Payment */}
                      <div className="mb-4 p-3 bg-gradient-to-r from-emerald-50 to-blue-50 dark:from-emerald-900/20 dark:to-blue-900/20 rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-300">Payment</span>
                          <span className="text-xl font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                            <DollarSign className="w-5 h-5" />
                            R{task.payment_amount}
                          </span>
                        </div>
                      </div>

                      {/* Details */}
                      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300 mb-4">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          <span className="truncate">{task.report?.neighborhood || task.report?.address}</span>
                        </div>
                        {task.claimed_date && (
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>Claimed {format(new Date(task.claimed_date), 'MMM d, yyyy')}</span>
                          </div>
                        )}
                        {task.created_date && !task.claimed_date && (
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>Assigned {format(new Date(task.created_date), 'MMM d, yyyy')}</span>
                          </div>
                        )}
                        {task.completed_date && (
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                            <span>Completed {format(new Date(task.completed_date), 'MMM d, yyyy')}</span>
                          </div>
                        )}
                        {task.payment_processed_date && (
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                            <span>Paid {format(new Date(task.payment_processed_date), 'MMM d, yyyy')}</span>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(createPageUrl(`ReportDetails?id=${task.report_id}`))}
                          className="flex-1"
                        >
                          View Details
                        </Button>
                        {task.status === 'claimed' && (
                          <Button
                            size="sm"
                            onClick={() => updateTaskStatusMutation.mutate({ taskId: task.id, status: 'in_progress' })}
                            disabled={updateTaskStatusMutation.isPending}
                            className="flex-1 bg-blue-600 hover:bg-blue-700"
                          >
                            Start Work
                          </Button>
                        )}
                        {task.status === 'in_progress' && (
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button
                                size="sm"
                                onClick={() => {
                                  setCompletingTask(task);
                                  setCompletionNotes("");
                                  setCompletionPhotos([]);
                                }}
                                className="flex-1 bg-green-600 hover:bg-green-700"
                              >
                                <CheckCircle2 className="w-4 h-4 mr-2" />
                                Complete
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Complete Task</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div>
                                  <Label>Completion Notes *</Label>
                                  <Textarea
                                    placeholder="Describe the work completed..."
                                    value={completionNotes}
                                    onChange={(e) => setCompletionNotes(e.target.value)}
                                    rows={3}
                                  />
                                </div>
                                <div>
                                  <Label>Before/After Photos</Label>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    onChange={handlePhotoUpload}
                                    className="hidden"
                                    id="completion-photos"
                                  />
                                  <label htmlFor="completion-photos">
                                    <Button
                                      type="button"
                                      variant="outline"
                                      className="w-full"
                                      disabled={uploadingPhotos}
                                      asChild
                                    >
                                      <span>
                                        <Upload className="w-4 h-4 mr-2" />
                                        {uploadingPhotos ? "Uploading..." : "Upload Photos"}
                                      </span>
                                    </Button>
                                  </label>
                                  {completionPhotos.length > 0 && (
                                    <div className="grid grid-cols-3 gap-2 mt-3">
                                      {completionPhotos.map((photo, idx) => (
                                        <img
                                          key={idx}
                                          src={photo}
                                          alt={`Completion ${idx + 1}`}
                                          className="w-full h-20 object-cover rounded border-2 border-emerald-200 dark:border-emerald-700"
                                        />
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <Button
                                  onClick={() => completeTaskMutation.mutate()}
                                  disabled={completeTaskMutation.isPending || !completionNotes.trim()}
                                  className="w-full bg-green-600 hover:bg-green-700"
                                >
                                  {completeTaskMutation.isPending ? (
                                    <>
                                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                      Submitting...
                                    </>
                                  ) : (
                                    "Submit for Payment"
                                  )}
                                </Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                        )}
                        {task.status === 'awaiting_payment' && (
                          <div className="flex-1 flex items-center justify-center text-sm text-amber-600 dark:text-amber-400">
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Processing payment...
                          </div>
                        )}
                      </div>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}